<template>
  <Card v-if="product" class="product-card">
    <CardHeader class="product-card-header">
      <div class="flex items-center gap-2 min-w-0">
        <Package :size="17" class="text-primary shrink-0" />
        <CardTitle class="product-name">{{ product.product }}</CardTitle>
      </div>
      <Button
        variant="ghost"
        size="icon"
        class="ml-auto shrink-0"
        aria-label="Настройки продукта"
        title="Настройки продукта"
        @click="showSettingsDialog = true"
      >
        <Settings2 :size="17" />
      </Button>
    </CardHeader>
    <CardContent class="product-card-body">
      <div v-if="product.info || latestVersion" class="product-facts">
        <div v-if="product.info" class="info-row" :class="getSupportClass()">
          <span class="info-label">Поддержка</span>
          <span class="fact-values">
            <Badge v-if="isSupportIssue()" variant="destructive">
              {{ product.info.support_suspended ? 'Приостановлена' : 'Просрочена' }}
            </Badge>
            <span v-else></span>
            <span class="num">{{ formatDate(product.info.support_till) }}</span>
          </span>
          <Hint label="Установка и поддержка">
            <p>Установка: {{ product.info.installer }}</p>
            <p v-if="product.createdDate">Дата установки: {{ formatDate(product.createdDate) }}</p>
            <p>Поддержка: {{ product.info.supporter }}</p>
          </Hint>
        </div>
        <div v-if="latestVersion" class="info-row">
          <span class="info-label">Версия</span>
          <span class="fact-values">
            <a
              v-if="latestVersion.url"
              :href="latestVersion.url"
              target="_blank"
              rel="noopener noreferrer"
              class="info-link num"
            >
              {{ latestVersion.version }}
            </a>
            <span v-else class="num">{{ latestVersion.version }}</span>
            <span class="num">{{ formatDate(latestVersion.created_at) }}</span>
          </span>
          <Button
            variant="ghost"
            size="icon"
            class="fact-action"
            :aria-expanded="showVersionHistory"
            aria-label="История версий"
            title="История версий"
            @click="showVersionHistory = !showVersionHistory"
          >
            <ChevronUp v-if="showVersionHistory" :size="15" />
            <ChevronDown v-else :size="15" />
          </Button>
        </div>
        <div v-if="product.info?.support_types?.length" class="info-row">
          <span class="info-label">Тип:</span>
          <div class="support-badges">
            <Badge
              v-for="supportType in product.info.support_types"
              :key="supportType"
              variant="secondary"
            >
              {{ supportType }}
            </Badge>
          </div>
          <span></span>
        </div>
        <div
          v-if="product.info && (product.info.support_24on7 || product.info.support_weekend)"
          class="info-row"
        >
          <span class="info-label">Режим:</span>
          <div class="support-badges">
            <Badge v-if="product.info.support_24on7" variant="outline">24/7</Badge>
            <Badge v-if="product.info.support_weekend" variant="outline">выходные</Badge>
          </div>
          <span></span>
        </div>
      </div>
      <div v-if="product.info?.support_notes" class="product-notes">
        <h3 class="section-small-title">Заметки</h3>
        <p class="notes-content">{{ product.info.support_notes }}</p>
      </div>
      <div v-if="product.modules?.length" class="product-modules">
        <h3 class="section-small-title">
          Модули
          <Badge variant="outline">{{ product.modules.length }}</Badge>
        </h3>
        <div class="modules-list">
          <div v-for="(section, secIdx) in moduleDisplaySections" :key="`mod-sec-${secIdx}`">
            <h4 v-if="section.heading" class="module-group-title">{{ section.heading }}</h4>
            <div v-for="module in section.modules" :key="module.id" class="module-row">
              <span
                class="mark"
                :class="{ 'mark-paid': module.is_paid }"
                :title="module.is_paid ? 'Платный' : undefined"
              >
                <template v-if="module.is_paid">₽</template>
              </span>
              <span class="module-name">{{ module.module_name }}</span>
              <span class="num module-dates" :title="moduleDateTitle(module)">
                {{ moduleDateText(module) }}
              </span>
              <span class="module-hint">
                <Hint
                  v-if="module.module_description || module.notes"
                  :label="module.notes ? 'Примечание' : 'Описание'"
                >
                  <p v-if="module.module_description">{{ module.module_description }}</p>
                  <p v-if="module.notes">{{ module.notes }}</p>
                </Hint>
              </span>
            </div>
          </div>
        </div>
      </div>
      <template v-if="product.versions.length && showVersionHistory">
        <div class="versions-header section-small-title">
          История версий
          <Badge variant="outline">{{ product.versions.length }}</Badge>
        </div>
        <label class="ui-field">
          <span class="ui-label">Поиск версии</span>
          <Input v-model="searchQuery" type="search" placeholder="Номер версии" />
        </label>
        <div class="versions-list">
          <div v-for="[year, group] in groupedVersions" :key="`year-${year}`">
            <div class="year-header">
              <span>{{ year }}</span>
              <Badge variant="outline">{{ group.length }}</Badge>
            </div>
            <div v-for="version in group" :key="version.version" class="version-row">
              <a
                v-if="version.url"
                :href="version.url"
                target="_blank"
                rel="noopener noreferrer"
                class="version-link"
              >
                {{ version.version }}
              </a>
              <span v-else>{{ version.version }}</span>
              <time class="num version-date" :title="formatDateTime(version.created_at)">
                {{ formatDate(version.created_at) }}
              </time>
            </div>
          </div>
        </div>
        <Button v-if="canShowMore" variant="ghost" size="sm" @click="toggleShowAll">
          {{ showAllVersions ? 'Скрыть' : `Показать все (${product.versions.length})` }}
        </Button>
      </template>
    </CardContent>
    <CardFooter v-if="product.versions.length" class="product-footer">
      <Button as-child variant="ghost" size="sm">
        <router-link
          :to="{
            name: 'text-info-changes',
            params: { propertyId: product.propertyId, product: product.product },
          }"
        >
          <FileCode2 :size="14" />
          Изменения параметров
        </router-link>
      </Button>
    </CardFooter>
    <property-product-settings-dialog
      v-if="product"
      v-model="showSettingsDialog"
      :property-id="product.propertyId"
      :product-id="product.productId"
      :product-name="product.product"
      :current-modules="product.modules"
      :support-info="product.info"
      @saved="emit('refresh')"
    />
  </Card>
</template>

<script setup lang="ts">
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Hint } from '@/components/ui/hint';
import { Input } from '@/components/ui/input';
import { Package, Settings2, ChevronUp, ChevronDown, FileCode2 } from '@lucide/vue';

import { ref, computed, watch } from 'vue';
import moment from 'moment';
import PropertyProductSettingsDialog from '@/components/PropertyProductSettingsDialog.vue';

interface Props {
  propertyDetailProduct: PropertyDetailProduct;
}

const props = defineProps<Props>();
const emit = defineEmits(['refresh']);

const product = ref<PropertyDetailProduct | null>(null);
const searchQuery = ref('');
const showAllVersions = ref(false);
const showVersionHistory = ref(false);
const showSettingsDialog = ref(false);
const maxVersionsToShow = 5;

watch(
  () => props.propertyDetailProduct,
  (newVal) => {
    if (newVal) {
      product.value = JSON.parse(JSON.stringify(newVal));
    } else {
      product.value = null;
    }
  },
  { immediate: true, deep: true }
);

function formatDate(date: string): string {
  if (!date) return '';
  return moment(date).format('DD.MM.YYYY');
}

function moduleDateText(module: PropertyModule): string {
  if (module.license_expires) return formatDate(module.license_expires);
  if (module.acquired_at) return formatDate(module.acquired_at);
  return '';
}

function moduleDateTitle(module: PropertyModule): string | undefined {
  const parts: string[] = [];
  if (module.license_expires) parts.push(`Лицензия до ${formatDate(module.license_expires)}`);
  if (module.acquired_at) parts.push(`Приобретён ${formatDate(module.acquired_at)}`);
  return parts.length ? parts.join('. ') : undefined;
}

function formatDateTime(dateTime: string): string {
  if (!dateTime) return '';
  return moment(dateTime).format('DD.MM.YYYY HH:mm');
}

function isSupportIssue(): boolean {
  if (!product.value || !product.value?.info) return false;
  return product.value.info.support_suspended || isOverdueSupport(product.value.info.support_till);
}

function isOverdueSupport(date: any): boolean {
  return moment(date).diff(Date.now()) < 0;
}

function getSupportClass(): string {
  if (!product.value || !product.value?.info) return '';
  if (product.value.info.support_suspended) {
    return 'support-suspended';
  }
  if (isOverdueSupport(product.value.info.support_till)) {
    return 'support-overdue';
  }
  return '';
}

const filteredVersions = computed((): ProductVersion[] => {
  if (!product.value) return [];

  let versions = [...product.value.versions];

  // Фильтрация по поиску
  if (searchQuery.value && searchQuery.value.trim()) {
    const query = searchQuery.value.toLowerCase();
    versions = versions.filter((v) => v.version.toLowerCase().includes(query));
  }

  // Сортировка по дате (новые сверху)
  versions.sort((a, b) => moment(b.created_at).valueOf() - moment(a.created_at).valueOf());

  // Ограничение количества, если не показываем все
  if (!showAllVersions.value && !searchQuery.value) {
    versions = versions.slice(0, maxVersionsToShow);
  }

  return versions;
});

const groupedVersions = computed((): Array<[string, ProductVersion[]]> => {
  const grouped: { [year: string]: ProductVersion[] } = {};

  filteredVersions.value.forEach((version) => {
    const year = moment(version.created_at).format('YYYY');
    if (!grouped[year]) {
      grouped[year] = [];
    }
    grouped[year].push(version);
  });

  // Сортируем года по убыванию (самые свежие сверху)
  return Object.entries(grouped).sort((a, b) => parseInt(b[0]) - parseInt(a[0]));
});

const canShowMore = computed((): boolean => {
  if (!product.value) return false;
  return product.value.versions.length > maxVersionsToShow && !searchQuery.value;
});

function toggleShowAll(): void {
  showAllVersions.value = !showAllVersions.value;
}

const latestVersion = computed((): ProductVersion | null => {
  if (!product.value || product.value.versions.length === 0) return null;

  // Sort versions by created_at and return the latest one
  const sortedVersions = [...product.value.versions].sort(
    (a, b) => moment(b.created_at).valueOf() - moment(a.created_at).valueOf()
  );

  return sortedVersions[0];
});

interface ModuleDisplaySection {
  heading: string;
  modules: PropertyModule[];
}

const moduleDisplaySections = computed((): ModuleDisplaySection[] => {
  if (!product.value?.modules?.length) return [];
  const list = product.value.modules;
  const withParent = list.filter((m) => m.module_parent_id != null);
  const orphans = list.filter((m) => m.module_parent_id == null);
  const groups = new Map<number, { heading: string; items: PropertyModule[] }>();
  for (const m of withParent) {
    const id = m.module_parent_id as number;
    if (!groups.has(id)) {
      groups.set(id, { heading: m.module_parent_name || `Группа ${id}`, items: [] });
    }
    groups.get(id)?.items.push(m);
  }
  const sections: ModuleDisplaySection[] = [...groups.values()]
    .map((g) => ({
      heading: g.heading,
      modules: [...g.items].sort((a, b) => a.module_name.localeCompare(b.module_name, 'ru')),
    }))
    .sort((a, b) => a.heading.localeCompare(b.heading, 'ru'));
  if (orphans.length) {
    sections.push({
      heading: orphans.length > 1 || sections.length > 0 ? 'Без группы' : '',
      modules: [...orphans].sort((a, b) => a.module_name.localeCompare(b.module_name, 'ru')),
    });
  }
  return sections.map((s) => ({
    heading: s.heading,
    modules: s.modules,
  }));
});
</script>
